import { NATURES } from '../data/natures.js';
import { multiplier } from '../data/type-chart.js';

/** ±10%, or 1 for the five neutral natures. Unaffected stat -> 1. */
export function natureMultiplier(natureName, statKey) {
  const n = NATURES.find((x) => x.name === natureName);
  if (!n || n.up === n.down) return 1;
  if (n.up === statKey) return 1.1;
  if (n.down === statKey) return 0.9;
  return 1;
}

/**
 * A real in-game stat from a base stat, level, IVs/EVs and nature — the
 * standard Gen 3+ formula (also close enough for earlier gens, which this
 * calculator otherwise already treats generically).
 */
export function statAt(base, level, { iv = 31, ev = 0, nature = 'Hardy', statKey } = {}) {
  if (statKey === 'hp') {
    if (base <= 1) return 1; // Shedinja
    return Math.floor(((2 * base + iv + Math.floor(ev / 4)) * level) / 100) + level + 10;
  }
  const raw = Math.floor(((2 * base + iv + Math.floor(ev / 4)) * level) / 100 + 5);
  return Math.floor(raw * natureMultiplier(nature, statKey));
}

/**
 * The standard damage formula, evaluated across the 85–100% random roll.
 * Ignores items, abilities, weather, terrain and spread-move reduction —
 * none of those are part of this app's data model, so treat the result as
 * a base-stats estimate rather than a battle-exact number.
 *
 * Returns null for status moves or 0-power moves.
 */
export function calcDamage({
  level,
  power,
  moveType,
  atkTypes,
  defTypes,
  atkStat,
  defStat,
  gen,
  crit = false,
}) {
  if (!power) return null;
  const base = Math.floor((Math.floor((2 * level) / 5 + 2) * power * (atkStat / defStat)) / 50) + 2;
  const stab = atkTypes.includes(moveType) ? 1.5 : 1;
  const eff = multiplier(moveType, defTypes, gen);
  const critMult = crit ? (gen <= 5 ? 2 : 1.5) : 1;
  if (eff === 0) return { min: 0, max: 0, stab: stab > 1, eff: 0 };
  const modifier = stab * eff * critMult;
  const min = Math.max(1, Math.floor(Math.floor((base * 85) / 100) * modifier));
  const max = Math.max(1, Math.floor(base * modifier));
  return { min, max, stab: stab > 1, eff };
}
